import { useCallback } from "react";

import { TOGGLE_LINK_COMMAND } from "@lexical/link";
import {
  INSERT_UNORDERED_LIST_COMMAND,
  INSERT_ORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
  $isListItemNode,
  $isListNode,
  $createListNode,
  ListItemNode,
  ListNode,
} from "@lexical/list";
import type { ListType } from "@lexical/list";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $patchStyleText } from "@lexical/selection";
import {
  FORMAT_TEXT_COMMAND,
  FORMAT_ELEMENT_COMMAND,
  $getSelection,
  $isRangeSelection,
  $isTextNode,
  $isParagraphNode,
  TextNode,
  ElementNode,
  $createRangeSelection,
  $setSelection,
  $getRoot,
  LexicalNode,
  $isElementNode,
  $getNodeByKey,
  $createParagraphNode,
  RangeSelection,
} from "lexical";

import { $getAllTextNodes } from "./helpers";

export const useRichTextToolbarCommand = () => {
  const [editor] = useLexicalComposerContext();

  const applyTextFormat = useCallback(
    (dispatchCommandCallback: () => void) => {
      editor.update(() => {
        const selection = $getSelection();

        // Collapsed / no selection → synthesise a whole-block range, format it, then collapse to the end.
        if (!$isRangeSelection(selection) || selection.isCollapsed()) {
          const root = $getRoot();
          const allTextNodes = $getAllTextNodes(root);
          // If we have text nodes, create a selection, apply format, then collapse it
          if (allTextNodes.length > 0) {
            const firstNode = allTextNodes[0];
            const lastNode = allTextNodes[allTextNodes.length - 1];
            const rangeSelection = $createRangeSelection();
            rangeSelection.setTextNodeRange(firstNode, 0, lastNode, lastNode.getTextContentSize());
            rangeSelection.format = firstNode.getFormat();
            $setSelection(rangeSelection);

            // Apply the format
            dispatchCommandCallback();

            $collapseSelectionToFocus();
          } else {
            // No text to format
            return;
          }
        } else {
          // Apply the format to the existing selection
          dispatchCommandCallback();
        }
      });
    },
    [editor]
  );

  // VariableNode is a token TextNode, so FORMAT_TEXT_COMMAND reaches it natively.
  const toggleBold = useCallback(
    () => applyTextFormat(() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "bold")),
    [applyTextFormat, editor]
  );
  const toggleItalic = useCallback(
    () => applyTextFormat(() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "italic")),
    [applyTextFormat, editor]
  );
  const toggleUnderline = useCallback(
    () => applyTextFormat(() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "underline")),
    [applyTextFormat, editor]
  );

  const toggleUnorderedList = useCallback(
    (isUnorderedList: boolean) => {
      editor.dispatchCommand(isUnorderedList ? REMOVE_LIST_COMMAND : INSERT_UNORDERED_LIST_COMMAND, undefined);
    },
    [editor]
  );

  const toggleOrderedList = useCallback(
    (isOrderedList: boolean) => {
      editor.dispatchCommand(isOrderedList ? REMOVE_LIST_COMMAND : INSERT_ORDERED_LIST_COMMAND, undefined);
    },
    [editor]
  );

  const onChangeFontSize = useCallback(
    (fontSize: string) => {
      editor.update(() => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) {
          return;
        }

        // VariableNode is a TextNode, so it is collected here alongside plain text.
        const textNodes: TextNode[] = [];
        for (const node of selection.getNodes()) {
          if ($isTextNode(node)) {
            textNodes.push(node);
          }
        }

        if (textNodes.length === 0) {
          const anchorNode = selection.anchor.getNode();
          if ($isTextNode(anchorNode)) {
            textNodes.push(anchorNode);
          }
        }

        for (const textNode of textNodes) {
          const currentStyle = textNode.getStyle();
          const styleWithoutFontSize = currentStyle ? currentStyle.replace(/font-size:\s*\d+px;?/gi, "").trim() : "";
          const newStyle = styleWithoutFontSize
            ? `${styleWithoutFontSize}; font-size: ${fontSize}px`
            : `font-size: ${fontSize}px`;
          textNode.setStyle(newStyle);
        }
      });
    },
    [editor]
  );

  const setTextColor = useCallback(
    (color: string) => {
      const trimmedColor = color.trim();
      const effectiveColor = trimmedColor && trimmedColor !== "transparent" ? trimmedColor : "";
      const patch = { color: effectiveColor };

      // Synthetic event keeps Lexical on `lastSelection` since the color picker steals DOM focus from the editor.
      editor.update(
        () => {
          const selection = $getSelection();
          if (!$isRangeSelection(selection)) {
            return;
          }

          // if no highlighted text, apply color to all text (VariableNode included, since it is a TextNode)
          if (selection.isCollapsed()) {
            const root = $getRoot();
            const allTextNodes = $getAllTextNodes(root);

            if (allTextNodes.length > 0) {
              const firstNode = allTextNodes[0];
              const lastNode = allTextNodes[allTextNodes.length - 1];
              const rangeSelection = $createRangeSelection();
              rangeSelection.setTextNodeRange(firstNode, 0, lastNode, lastNode.getTextContentSize());
              rangeSelection.format = firstNode.getFormat();
              $setSelection(rangeSelection);

              // Use $patchStyleText to apply color only to the selected text
              // This automatically handles splitting text nodes at selection boundaries
              $patchStyleText(rangeSelection, patch);

              $collapseSelectionToFocus();
            }
            return;
          }

          $patchStyleText(selection, patch);
        },
        { event: { type: "custom" } as Event }
      );
    },
    [editor]
  );

  const setLineHeight = useCallback(
    (lineHeight: string) => {
      editor.update(
        () => {
          const selection = $getSelection();
          const blocks = new Set<ElementNode>();

          if ($isRangeSelection(selection) && !selection.isCollapsed()) {
            for (const node of selection.getNodes()) {
              const block = findBlockAncestor(node);
              if (block) {
                blocks.add(block);
              }
            }
          }

          if (blocks.size === 0) {
            const root = $getRoot();
            collectAllBlocks(root, blocks);
          }

          for (const block of blocks) {
            const currentStyle = block.getStyle();
            const withoutLineHeight = currentStyle
              ? currentStyle.replace(/line-height:\s*[^;]+;?\s*/gi, "").trim()
              : "";
            const newStyle = withoutLineHeight
              ? `${withoutLineHeight}; line-height: ${lineHeight}`
              : `line-height: ${lineHeight}`;
            block.setStyle(newStyle);
          }
        },
        { event: { type: "custom" } as Event }
      );
    },
    [editor]
  );

  const toggleLink = useCallback(
    (url: string | null) => {
      if (!url || url.trim() === "") {
        editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
        return;
      }

      let finalUrl = url.trim();
      if (!finalUrl.match(/^(https?|mailto|tel|sms):/i)) {
        finalUrl = `https://${finalUrl}`;
      }

      editor.dispatchCommand(TOGGLE_LINK_COMMAND, {
        url: finalUrl,
        target: "_blank",
      });
    },
    [editor]
  );

  const setTextAlign = useCallback(
    (alignment: "left" | "center" | "right" | "justify") => {
      applyTextFormat(() => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, alignment));
    },
    [applyTextFormat, editor]
  );

  const preserveSelection = useCallback(() => {
    let savedAnchorKey: string | null = null;
    let savedAnchorOffset: number | null = null;
    let savedAnchorType: "text" | "element" | null = null;
    let savedFocusKey: string | null = null;
    let savedFocusOffset: number | null = null;
    let savedFocusType: "text" | "element" | null = null;

    editor.getEditorState().read(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        savedAnchorKey = selection.anchor.key;
        savedAnchorOffset = selection.anchor.offset;
        savedAnchorType = selection.anchor.type;
        savedFocusKey = selection.focus.key;
        savedFocusOffset = selection.focus.offset;
        savedFocusType = selection.focus.type;
      }
    });

    return (restoreFocus: boolean = true) => {
      if (
        savedAnchorKey !== null &&
        savedAnchorOffset !== null &&
        savedAnchorType !== null &&
        savedFocusKey !== null &&
        savedFocusType !== null &&
        savedFocusOffset !== null
      ) {
        editor.update(() => {
          // Try to restore the selection using the saved anchor and focus points
          try {
            const anchorNode = $getNodeByKey(savedAnchorKey!);
            const focusNode = $getNodeByKey(savedFocusKey!);

            if (anchorNode && focusNode) {
              const rangeSelection = $createRangeSelection();
              rangeSelection.anchor.set(savedAnchorKey!, savedAnchorOffset!, savedAnchorType!);
              rangeSelection.focus.set(savedFocusKey!, savedFocusOffset!, savedFocusType!);
              $setSelection(rangeSelection);
            }
          } catch {
            // Swallow: saved keys can be invalidated by intervening content edits.
          }
        });
        if (restoreFocus) {
          const rootElement = editor.getRootElement();
          if (rootElement) {
            rootElement.focus();
          }
        }
      }
    };
  }, [editor]);

  // Visual-only highlight kept while the editor loses focus (e.g. color picker open); `historic` tag keeps it out of undo.
  const highlightSelection = useCallback(
    (highlightColor: string = "rgba(59, 130, 246, 0.3)") => {
      editor.update(
        () => {
          const selection = $getSelection();
          if (!$isRangeSelection(selection) || selection.isCollapsed()) {
            return;
          }

          // eslint-disable-next-line @typescript-eslint/naming-convention
          $patchStyleText(selection, { "background-color": highlightColor, "--lexical-highlight": "1" });
        },
        { tag: "historic" }
      );

      // Return a cleanup function to remove the highlight
      return () => {
        editor.update(
          () => {
            const root = $getRoot();

            const cleanHighlightedNodes = (node: LexicalNode) => {
              if ($isTextNode(node)) {
                const style = node.getStyle();
                if (style.includes("--lexical-highlight")) {
                  const cleanedStyle = style
                    .replace(/--lexical-highlight:\s*1;?\s*/g, "")
                    .replace(/background-color:\s*[^;]+;?\s*/g, "")
                    .trim()
                    .replace(/^;\s*/, "")
                    .replace(/;\s*$/, "");
                  node.setStyle(cleanedStyle);
                }
              } else if ($isElementNode(node)) {
                node.getChildren().forEach(cleanHighlightedNodes);
              }
            };

            root.getChildren().forEach(cleanHighlightedNodes);
          },
          { tag: "historic" }
        );
      };
    },
    [editor]
  );

  const changeSelectedItemsListType = useCallback(
    (targetType: ListType) => {
      editor.update(() => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) {
          return;
        }

        const selectedItems = getSelectedListItems(selection);
        if (selectedItems.length === 0) {
          return;
        }

        const groups = groupItemsByParentList(selectedItems);

        for (const { parent, items } of groups.values()) {
          if (parent.getListType() === targetType) {
            continue;
          }

          const { selected, after } = splitChildrenBySelection(parent, items);

          if (selected.length === parent.getChildrenSize()) {
            parent.setListType(targetType);
            continue;
          }

          const newList = $createListNode(targetType);
          parent.insertAfter(newList);

          if (after.length > 0) {
            const afterList = $createListNode(parent.getListType());
            newList.insertAfter(afterList);
            for (const item of after) {
              afterList.append(item);
            }
          }

          for (const item of selected) {
            newList.append(item);
          }

          if (parent.getChildrenSize() === 0) {
            parent.remove();
          }
        }
      });
    },
    [editor]
  );

  const removeSelectedItemsFromList = useCallback(() => {
    editor.update(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) {
        return;
      }

      const selectedItems = getSelectedListItems(selection);
      if (selectedItems.length === 0) {
        return;
      }

      const groups = groupItemsByParentList(selectedItems);

      for (const { parent, items } of groups.values()) {
        const { selected, after } = splitChildrenBySelection(parent, items);

        let anchor: LexicalNode = parent;
        for (const item of selected) {
          if (!$isListItemNode(item)) {
            continue;
          }
          const paragraph = $createParagraphNode();
          paragraph.setFormat(item.getFormatType());
          const children = item.getChildren();
          for (const child of children) {
            paragraph.append(child);
          }
          anchor.insertAfter(paragraph);
          anchor = paragraph;
          item.remove();
        }

        if (after.length > 0) {
          const afterList = $createListNode(parent.getListType());
          anchor.insertAfter(afterList);
          for (const item of after) {
            afterList.append(item);
          }
        }

        if (parent.getChildrenSize() === 0) {
          parent.remove();
        }
      }
    });
  }, [editor]);

  return {
    toggleBold,
    toggleItalic,
    toggleUnderline,
    toggleOrderedList,
    toggleUnorderedList,
    onChangeFontSize,
    setTextColor,
    setLineHeight,
    toggleLink,
    setTextAlign,
    preserveSelection,
    highlightSelection,
    changeSelectedItemsListType,
    removeSelectedItemsFromList,
  };
};

const $collapseSelectionToFocus = (): void => {
  const selection = $getSelection();
  if (!$isRangeSelection(selection)) {
    return;
  }
  const { key, offset, type } = selection.focus;
  selection.anchor.set(key, offset, type);
};

const isLineBlock = (node: LexicalNode): node is ElementNode => $isParagraphNode(node) || $isListItemNode(node);

const findBlockAncestor = (node: LexicalNode): ElementNode | null => {
  let current: LexicalNode | null = node;
  while (current !== null) {
    if (isLineBlock(current)) {
      return current;
    }
    current = current.getParent();
  }
  return null;
};

const collectAllBlocks = (parent: LexicalNode, out: Set<ElementNode>): void => {
  if (isLineBlock(parent)) {
    out.add(parent);
  }
  if ($isElementNode(parent)) {
    for (const child of parent.getChildren()) {
      collectAllBlocks(child, out);
    }
  }
};

const findListItemAncestor = (node: LexicalNode): ListItemNode | null => {
  let current: LexicalNode | null = node;
  while (current !== null) {
    if ($isListItemNode(current)) {
      return current;
    }
    current = current.getParent();
  }
  return null;
};

const getSelectedListItems = (selection: RangeSelection): ListItemNode[] => {
  const nodes = selection.getNodes();
  const seen = new Set<string>();
  const result: ListItemNode[] = [];

  for (const node of nodes) {
    const listItem = findListItemAncestor(node);
    if (listItem && !seen.has(listItem.getKey())) {
      seen.add(listItem.getKey());
      result.push(listItem);
    }
  }

  const anchorItem = findListItemAncestor(selection.anchor.getNode());
  if (anchorItem && !seen.has(anchorItem.getKey())) {
    seen.add(anchorItem.getKey());
    result.push(anchorItem);
  }

  return result;
};

const groupItemsByParentList = (items: ListItemNode[]): Map<string, { parent: ListNode; items: ListItemNode[] }> => {
  const groups = new Map<string, { parent: ListNode; items: ListItemNode[] }>();
  for (const item of items) {
    const parent = item.getParent();
    if (!$isListNode(parent)) {
      continue;
    }
    const key = parent.getKey();
    if (!groups.has(key)) {
      groups.set(key, { parent, items: [] });
    }
    groups.get(key)!.items.push(item);
  }
  return groups;
};

const splitChildrenBySelection = (
  parent: ListNode,
  selectedItems: ListItemNode[]
): { before: LexicalNode[]; selected: LexicalNode[]; after: LexicalNode[] } => {
  const allChildren = parent.getChildren();
  const selectedKeys = new Set(selectedItems.map((i) => i.getKey()));

  let minIndex = allChildren.length;
  let maxIndex = -1;

  for (let i = 0; i < allChildren.length; i++) {
    if (selectedKeys.has(allChildren[i].getKey())) {
      minIndex = Math.min(minIndex, i);
      maxIndex = Math.max(maxIndex, i);
    }
  }

  return {
    before: allChildren.slice(0, minIndex),
    selected: allChildren.slice(minIndex, maxIndex + 1),
    after: allChildren.slice(maxIndex + 1),
  };
};
